import type { ThemeOptions } from '@mui/material'

import { primitives } from './tokens/primitives'
import { SemanticTokens } from './tokens/semantic'
import { pxToRem, typeStyle } from './typeStyle'

const { family, style } = primitives.font
const { control, icon } = primitives.size

const px = (value: number) => `${value}px`

/**
 * MUI component overrides of the new experience, from the design system specs. MUI `small` and
 * `medium` controls both get the design system's general height (36px): legacy code uses `small`
 * as its default size.
 */
export const nextComponents = (tokens: SemanticTokens): ThemeOptions['components'] => {
  const { bg, text, border, action, status, elevation, focusRing } = tokens

  const disabledFill = {
    backgroundColor: action.disabled.bg,
    color: action.disabled.text,
    borderColor: 'transparent'
  }
  const secondaryButton = {
    backgroundColor: action.secondary.bg,
    color: action.secondary.text,
    border: `1px solid ${action.secondary.border}`,
    '&:hover': {
      backgroundColor: action.secondary.bgHover,
      border: `1px solid ${action.secondary.border}`
    },
    '&.Mui-disabled': disabledFill
  }
  const ghostButton = {
    color: action.ghost.text,
    '&:hover': { backgroundColor: action.ghost.bgHover }
  }
  const alert = (tone: keyof SemanticTokens['status']) => ({
    backgroundColor: status[tone].bg,
    color: text.primary,
    boxShadow: `inset 0 0 0 1px ${status[tone].border}`,
    '& .MuiAlert-icon': { color: status[tone].icon },
    '& .MuiAlertTitle-root': { color: status[tone].text }
  })
  const menuOption = {
    ...typeStyle(style.bodyMdRegular),
    minHeight: px(control.md),
    padding: '8px 12px',
    color: text.primary
  }

  return {
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': { boxShadow: focusRing },
          // Rows, tabs and segments sit next to or inside something that hides an outer ring
          [[
            '&.MuiListItem-button.Mui-focusVisible',
            '&.MuiListItemButton-root.Mui-focusVisible',
            '&.MuiTab-root.Mui-focusVisible',
            '&.MuiToggleButton-root.Mui-focusVisible'
          ].join(', ')]: { boxShadow: `inset 0 0 0 2px ${border.focus}` }
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          ...typeStyle(style.labelButton),
          minHeight: px(control.md),
          minWidth: 0,
          padding: '0 16px',
          borderRadius: 0,
          textTransform: 'none',
          boxShadow: 'none',
          // MUI's disableElevation clears the focus shadow that ButtonBase sets
          '&.Mui-focusVisible': { boxShadow: focusRing },
          '&.Mui-disabled': { color: action.disabled.text }
        },
        sizeLarge: { minHeight: px(control.lg), padding: '0 24px' },
        containedPrimary: {
          backgroundColor: action.primary.bg,
          color: action.primary.text,
          '&:hover': { backgroundColor: action.primary.bgHover, boxShadow: 'none' },
          '&:active': { backgroundColor: action.primary.bgPressed },
          '&.Mui-disabled': disabledFill
        },
        containedSecondary: secondaryButton,
        containedError: {
          backgroundColor: action.danger.bg,
          color: action.danger.text,
          '&:hover': { backgroundColor: action.danger.bgHover, boxShadow: 'none' },
          '&.Mui-disabled': disabledFill
        },
        outlinedPrimary: secondaryButton,
        outlinedSecondary: secondaryButton,
        outlinedError: {
          ...secondaryButton,
          color: status.error.text,
          '&:hover': { ...secondaryButton['&:hover'], color: status.error.text }
        },
        textPrimary: ghostButton,
        textSecondary: ghostButton,
        textError: {
          color: status.error.text,
          '&:hover': { backgroundColor: action.ghost.bgHover }
        },
        colorInherit: { color: 'inherit' },
        iconSizeSmall: { '& > *:first-of-type': { fontSize: pxToRem(icon.sm) } },
        iconSizeMedium: { '& > *:first-of-type': { fontSize: pxToRem(icon.sm) } },
        iconSizeLarge: { '& > *:first-of-type': { fontSize: pxToRem(icon.md) } }
      }
    },
    MuiIconButton: {
      // MUI's translucent hover is kept: icon buttons also sit on widget headers colored by app
      // builders
      styleOverrides: { root: { borderRadius: 0 } }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          '& .MuiButton-root:hover, & .MuiIconButton-root:hover': {
            backgroundColor: action.inverse.bgHover
          },
          '& .MuiButton-root.Mui-disabled': { backgroundColor: 'transparent' }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: bg.surface, backgroundImage: 'none' },
        rounded: { borderRadius: 0 },
        outlined: { borderColor: border.subtle }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: primitives.color.scrim } }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 0, boxShadow: elevation[3], backgroundImage: 'none' }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { ...typeStyle(style.headingLg), color: text.primary, padding: '20px 16px 8px 24px' }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          ...typeStyle(style.bodyMdRegular),
          fontFamily: family.sans,
          color: text.primary,
          padding: '8px 24px 24px'
        },
        dividers: { borderTop: 'none', borderBottomColor: border.subtle, padding: '8px 24px 24px' }
      }
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '16px 24px' } }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: bg.surface,
          color: text.primary,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: border.control },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: border.controlHover },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: border.focus,
            borderWidth: primitives.borderWidth.strong
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: border.error },
          '&.Mui-disabled': {
            backgroundColor: action.disabled.bg,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: border.subtle }
          }
        },
        notchedOutline: { '& legend': { fontSize: pxToRem(style.captionRegular.fontSize) } },
        adornedStart: { paddingLeft: '12px' },
        adornedEnd: { paddingRight: '12px' },
        multiline: { padding: '8px 12px' },
        input: {
          ...typeStyle(style.bodyMdRegular),
          padding: '8px 12px',
          '&::placeholder': { color: text.disabled, opacity: 1 }
        },
        inputSizeSmall: { padding: '8px 12px' },
        inputMultiline: { padding: 0 },
        inputAdornedStart: { paddingLeft: 0 },
        inputAdornedEnd: { paddingRight: 0 }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: text.secondary,
          '&.Mui-focused': { color: border.focus },
          '&.Mui-error': { color: status.error.text },
          '&.Mui-disabled': { color: text.disabled }
        },
        outlined: {
          '&:not(.MuiInputLabel-shrink)': { transform: 'translate(12px, 8px) scale(1)' },
          // Shrunk to caption size, which the outline's notch (legend) is sized to
          '&.MuiInputLabel-shrink': {
            transform: 'translate(13px, -9px) scale(0.857)',
            maxWidth: 'calc(116% - 28px)'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...typeStyle(style.captionRegular),
          color: text.secondary,
          '&.Mui-error': { color: status.error.text }
        }
      }
    },
    MuiSelect: {
      styleOverrides: { icon: { color: text.primary } }
    },
    MuiPopover: {
      styleOverrides: {
        paper: { border: `1px solid ${border.default}`, boxShadow: elevation[2] }
      }
    },
    MuiMenu: {
      styleOverrides: { list: { paddingTop: '4px', paddingBottom: '4px' } }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...menuOption,
          // MUI resets the min height to auto from the sm breakpoint up, in a media query that
          // is emitted after these rules
          '&&': { minHeight: px(control.md) },
          '&:hover, &.Mui-focusVisible': { backgroundColor: action.ghost.bgHover },
          // MUI flags the selected option as focus-visible even when the menu opens by mouse
          '&.Mui-focusVisible': { boxShadow: 'none' },
          '&.Mui-selected': {
            backgroundColor: bg.subtle,
            fontWeight: primitives.font.weight.medium,
            '&:hover, &.Mui-focusVisible': { backgroundColor: action.ghost.bgHover }
          },
          '&.Mui-disabled': { color: text.disabled, opacity: 1 }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingTop: '4px',
            paddingBottom: '4px',
            paddingLeft: '4px',
            '& .MuiAutocomplete-input': { padding: '4px 4px 4px 8px' }
          }
        },
        popupIndicator: { color: text.primary },
        paper: { border: `1px solid ${border.default}`, boxShadow: elevation[2] },
        listbox: {
          paddingTop: '4px',
          paddingBottom: '4px',
          '& .MuiAutocomplete-option': {
            ...menuOption,
            '&.Mui-focused': { backgroundColor: action.ghost.bgHover },
            '&[aria-selected="true"]': {
              backgroundColor: bg.subtle,
              fontWeight: primitives.font.weight.medium,
              '&.Mui-focused': { backgroundColor: action.ghost.bgHover }
            }
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: border.control,
          // The ring hugs the box: the 38px touch target would overlap the label
          '&.Mui-focusVisible': {
            boxShadow: 'none',
            '& .MuiSvgIcon-root': { boxShadow: focusRing }
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: border.control,
          // Same as the checkbox; the first icon is the ring, the second the checked dot
          '&.Mui-focusVisible': {
            boxShadow: 'none',
            '& .MuiSvgIcon-root:first-of-type': { boxShadow: focusRing, borderRadius: '50%' }
          }
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        // 36×20 like the design system's toggle; the margin keeps MUI's 58×38 footprint
        root: {
          width: '36px',
          height: '20px',
          padding: 0,
          margin: '9px 11px',
          overflow: 'visible'
        },
        // MUI's small switch pads the thumb 4px through a descendant rule that outranks switchBase
        sizeSmall: { margin: '2px 6px', '& .MuiSwitch-switchBase': { padding: '2px' } },
        switchBase: {
          padding: '2px',
          color: bg.surface,
          '&:hover, &.Mui-checked:hover': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible': { boxShadow: 'none' },
          '&.Mui-focusVisible + .MuiSwitch-track': { boxShadow: focusRing },
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: bg.surface,
            '& + .MuiSwitch-track': { backgroundColor: action.primary.bg, opacity: 1 }
          },
          '&.Mui-disabled': { color: bg.surface },
          '&.Mui-disabled + .MuiSwitch-track': { backgroundColor: action.disabled.bg, opacity: 1 }
        },
        thumb: { width: '16px', height: '16px', boxShadow: 'none' },
        track: { borderRadius: '10px', backgroundColor: border.control, opacity: 1 }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          // Baseline drawn inside the bar, so the indicator covers it and the height stays the same
          '&:not(.MuiTabs-vertical)': { boxShadow: `inset 0 -1px 0 ${border.subtle}` }
        },
        indicator: { backgroundColor: bg.brand }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...typeStyle(style.bodyMdRegular),
          minHeight: '40px',
          padding: '0 12px',
          textTransform: 'none',
          color: text.secondary,
          '&:hover': { color: text.primary },
          '&.Mui-selected': { color: text.primary, fontWeight: primitives.font.weight.medium }
        }
      }
    },
    // The design system's SegmentedControl
    MuiToggleButton: {
      styleOverrides: {
        root: {
          ...typeStyle(style.labelButton),
          minHeight: '32px',
          padding: '0 12px',
          borderRadius: 0,
          borderColor: border.default,
          textTransform: 'none',
          color: text.primary,
          '&:hover': { backgroundColor: action.ghost.bgHover },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: action.primary.bg,
            color: action.primary.text
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...typeStyle(style.captionMedium),
          backgroundColor: bg.inverse,
          color: text.inverse,
          padding: '6px 10px',
          borderRadius: 0,
          border: 'none',
          boxShadow: 'none'
        },
        arrow: { color: bg.inverse }
      }
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 0 } }
    },
    MuiLink: {
      variants: [{ props: { color: 'primary' }, style: { color: text.link } }]
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${border.subtle}` },
        head: {
          ...typeStyle(style.captionMedium),
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: text.secondary,
          backgroundColor: bg.subtle
        },
        body: { ...typeStyle(style.bodySmRegular), color: text.primary },
        stickyHeader: { backgroundColor: bg.subtle }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 0 },
        standardSuccess: alert('success'),
        standardWarning: alert('warning'),
        standardError: alert('error'),
        standardInfo: alert('info')
      }
    }
  }
}
